import { useMemo, useState } from "react";
import { ClickAwayListener, Tooltip, TooltipProps } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  FONT_SIZE_SMALL,
  BOX_SHADOW_TOOLTIP,
} from "../../../constants/constants";
import {
  darkModeTextColor,
  darkModeModalBackgroundColor,
  muiButtonText,
} from "constants/colors";
import { useIsDarkMode } from "store/atoms";

const useStylesDarkMode = makeStyles({
  tooltip: {
    transformOrigin: "center top",
    margin: 0,
    backgroundColor: darkModeModalBackgroundColor,
    color: darkModeTextColor,
    fontSize: FONT_SIZE_SMALL,
    boxShadow: BOX_SHADOW_TOOLTIP,
    maxWidth: "unset",
  },
});

const useStylesLightMode = makeStyles({
  popper: {
    top: `${16}px !important`,
  },
  tooltip: {
    transformOrigin: "center top",
    margin: 0,
    backgroundColor: "#fff",
    color: muiButtonText,
    fontSize: FONT_SIZE_SMALL,
    boxShadow: BOX_SHADOW_TOOLTIP,
    maxWidth: "unset",
  },
});

export const HtmlTooltip = ({
  isShiftPicker = false,
  title,
  children,
  scale,
  enterTouchDelay,
  enterDelay,
  openOnMouseover = false,
  leaveDelay = 0,
  popperStyles,
  stickyOpen,
  allowPropagation = false,
  ...rest
}: {
  isShiftPicker?: boolean;
  title: string | React.JSX.Element;
  children: React.JSX.Element;
  scale?: number | null;
  enterTouchDelay?: number | null;
  enterDelay?: number | null;
  openOnMouseover?: boolean;
  /** * careful, sertting to 0 can cause potential infinite flicker */
  leaveDelay?: number | null;
  popperStyles?: React.CSSProperties;
  stickyOpen?: boolean;
  allowPropagation?: boolean;
} & Partial<TooltipProps>) => {
  const [open, setOpen] = useState<"closed" | "open" | "stuckOpen">("closed");

  const [isDarkMode] = useIsDarkMode();
  const classesDarkMode = useStylesDarkMode();
  const classesLightMode = useStylesLightMode();
  const classes = useMemo(
    () => (isDarkMode ? classesDarkMode : classesLightMode),
    [isDarkMode, classesDarkMode, classesLightMode]
  );
  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) {
          setOpen("closed");
        }
      }}
    >
      <span style={{ width: "100%", height: "100%", display: "inherit" }}>
        <Tooltip
          // if enterDelay prop is undefined, click to open/close
          // if enterDelay is specified, mouseover to open as usual
          {...(enterDelay === undefined
            ? {
                open: open !== "closed",
                onClick: e => {
                  if (!allowPropagation) {
                    e.stopPropagation();
                  }
                  setOpen(prev => {
                    if (prev === "stuckOpen") {
                      return "closed";
                    } else {
                      return stickyOpen ? "stuckOpen" : "open";
                    }
                  });
                },
                onMouseOver: openOnMouseover
                  ? () => setOpen("open")
                  : undefined,
                onMouseOut: openOnMouseover
                  ? () => {
                      if (open === "open") {
                        setTimeout(() => {
                          setOpen("closed");
                        }, leaveDelay ?? 100); // minimum leaveDelay prevents potential infinite flicker
                      }
                    }
                  : undefined,
              }
            : {
                enterDelay,
              })}
          enterTouchDelay={Number(enterTouchDelay)}
          leaveDelay={Number(leaveDelay)}
          classes={{ tooltip: classes.tooltip }}
          style={{
            ...(scale ? { transform: `scale(${scale}) !important` } : {}),
          }}
          PopperProps={{
            style: popperStyles,
          }}
          title={title as string}
          {...rest}
        >
          {children}
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};
